import html2PDF from 'jspdf-html2canvas'
import printJS from 'print-js'
import React, { ReactElement, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Button from 'ui/atoms/Button/Button'

type PrintButtonProps = {
  title?: string
  fileName?: string
  fitOverflowY?: {
    overflowInParentElement?: boolean
  }
  hideSelectors?: string[]
}

const htmlElementToBase64Pdf = async (htmlElement: HTMLElement): Promise<string> => {
  const pdf = await html2PDF(htmlElement, {
    margin: {
      top: 40,
      right: 40,
      bottom: 40,
      left: 40,
    },
    success: () => undefined,
  })
  return btoa(pdf.output())
}

export default function PrintButton({
  title: _title,
  fileName: documentTitle = '',
  hideSelectors = [],
}: PrintButtonProps): ReactElement {
  const { t } = useTranslation()

  const [printInProgress, setPrintInProgress] = useState(false)
  const title = _title || t('Print')

  const hideElements = (selectors: string[]): HTMLElement[] => {
    const hiddenElements: HTMLElement[] = []
    selectors.forEach((selector) => {
      document.querySelectorAll<HTMLElement>(selector).forEach((el) => {
        el.dataset._printDisplay = el.style.display
        el.style.display = 'none'
        hiddenElements.push(el)
      })
    })
    return hiddenElements
  }

  const restoreElements = (elements: HTMLElement[]): void => {
    elements.forEach((el) => {
      el.style.display = el.dataset._printDisplay || ''
      delete el.dataset._printDisplay
    })
  }

  return (
    <Button
      disabled={printInProgress}
      onClick={async () => {
        if (printInProgress) {
          return
        }
        setPrintInProgress(true)

        const hiddenElements = hideElements(hideSelectors)

        const htmlElements = document.querySelectorAll<HTMLElement>('[data-printable]')
        if (!htmlElements.length) {
          restoreElements(hiddenElements)
          setPrintInProgress(false)
          return
        }

        const printable = await htmlElementToBase64Pdf(htmlElements[0])

        printJS({
          printable,
          type: 'pdf',
          base64: true,
          documentTitle,
        })
        restoreElements(hiddenElements)
        setPrintInProgress(false)
      }}
      secondary
      large
      marginRight
      icon={`far fa-file-image`}
    >
      {title}
    </Button>
  )
}
